/**
 * 類似日検索 hook。
 *
 * SearchSimilarDays BFF RPC を呼んで候補日リストを取得する。
 * caseId / areaCode / targetDate のいずれかが未定義の場合は fetch を無効化する。
 * enabled=false の間はキャッシュ済みデータを返す（staleTime: 0）。
 */
import { create } from '@bufbuild/protobuf'
import type { MessageShape } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { searchSimilarDays } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import {
  SearchSimilarDaysRequestSchema,
  SearchSimilarDaysResponseSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

type Params = {
  /** ワークフローケース ID */
  caseId: string | undefined
  /** 業務エリアコード */
  areaCode: AreaCode | undefined
  /** 予測対象日 */
  targetDate: ProtoDate | undefined
  /** 遡及検索日数 (0 の場合は backend 既定の 30 日) */
  lookbackDays?: number
  /** 自動選択件数 (0 = 自動選択なし, 最大 5) */
  autoSelectCount?: number
  /** true のときのみ fetch を実行する */
  enabled?: boolean
}

/**
 * 類似日候補リストを取得する。
 * enabled=false（既定）のため、caller が true に変更するまで fetch しない。
 *
 * 取得失敗はダイアログ内に出して再試行させる (issue #10429)。 error boundary
 * (全画面エラー) に飛ばすと編集中の需要想定ごと画面が消えるため、 global
 * QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする
 * (docs/guides/frontend-coding-guide.yaml「queries は throwOnError: true」からの意図的な逸脱)。
 */
export function useSearchSimilarDays({
  caseId,
  areaCode,
  targetDate,
  lookbackDays = 0,
  autoSelectCount = 3,
  enabled = false,
}: Params): UseQueryResult<
  MessageShape<typeof SearchSimilarDaysResponseSchema>,
  ConnectError
> {
  const canFetch =
    enabled &&
    caseId !== undefined &&
    caseId !== '' &&
    areaCode !== undefined &&
    targetDate !== undefined

  return useQuery(
    searchSimilarDays,
    create(SearchSimilarDaysRequestSchema, {
      caseId: caseId ?? '',
      areaCode: areaCode ?? 0,
      targetDate,
      lookbackDays,
      autoSelectCount,
    }),
    {
      enabled: canFetch,
      staleTime: 0,
      throwOnError: false,
    },
  )
}
